import { Component, computed, HostListener, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { authActions } from 'angular/app/core/store/auth';
import { fromStore } from '../../core/store';
import { Router } from '@angular/router';
import { firstValueFrom, skipWhile } from 'rxjs';
import { selectAuthState } from '../../core/store/reducers';
import { UserRole } from '../../core/models/entities/role';
import { ModalInstance, ModalService } from '../../core/services/modal.service';
import { bookingsActions } from '../../core/store/bookings';
import { VendorSelectorModalComponent } from '../../shared-manager/components/vendor-selector-modal/vendor-selector-modal.component';

@Component({
  selector: 'app-manager-layout',
  template: `
    <app-test-mode-header [vendor]="authUser()?.vendor"></app-test-mode-header>
    <div>
      <!-- -------------------------------------------------------------------------------------------------------->
      <!-- Static Navbar for mobile -->
      <!-- -------------------------------------------------------------------------------------------------------->
      <app-navbar class="lg:hidden"></app-navbar>
      <!-- -------------------------------------------------------------------------------------------------------->
      <!-- Static sidebar for desktop -->
      <!-- -------------------------------------------------------------------------------------------------------->
      <div
        class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col"
      >
        <!-- Sidebar component -->
        <div
          class="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white"
        >
          <div class="flex flex-1 flex-col overflow-y-auto">
            <!-- Header de la sidebar -->
            <div class="flex shrink-0 items-center gap-3 bg-gray-100 px-4 py-3">
              @if (authUser()) {
                <img
                  class="h-8 w-auto cursor-pointer"
                  [routerLink]="['/louer-salle', authUser()?.vendor?.slug]"
                  src="/assets/img/icon-secondary.svg"
                  alt="location-salles.ch"
                />
              }
              <div
                class="relative flex grow items-center gap-2"
                [class.cursor-pointer]="vendors().length > 1"
                (click)="openVendorSelectorModal()"
              >
                <div class="line-clamp-2 grow text-sm">
                  <div class="text-gray-500">{{ authUser().first_name }}</div>
                  <div class="font-medium">{{ authUser()?.vendor?.name }}</div>
                </div>
                @if (vendors().length > 1) {
                  <div
                    class="flex size-8 flex-none items-center justify-center rounded-full transition-colors hover:bg-gray-300"
                  >
                    <i class="fal fa-arrow-right-arrow-left text-xs"></i>
                  </div>
                }
              </div>
            </div>

            <!-- Navigation -->
            <nav class="flex-1">
              <app-sidebar-links></app-sidebar-links>
            </nav>
          </div>
        </div>
      </div>

      <div class="mt-16 flex flex-1 flex-col lg:mt-0 lg:pl-72">
        <main class="min-h-screen flex-1 bg-gray-50">
          <div class="py-6">
            <div class="mx-auto mt-2.5 mb-8 px-5 sm:px-6 lg:px-8">
              <router-outlet></router-outlet>
            </div>
          </div>
        </main>
      </div>
    </div>
  `,
  styles: [],
  standalone: false,
})
export class ManagerLayoutComponent {
  private modalService = inject(ModalService);
  private store = inject(Store);
  private router = inject(Router);

  // Signals
  authUser = this.store.selectSignal(fromStore.selectAuthUser);
  vendors = computed(() => {
    const user = this.authUser();
    return user?.vendors
      ? [...user.vendors].sort((a, b) => a.name.localeCompare(b.name))
      : [];
  });

  // Raccourci clavier Cmd+K pour ouvrir le vendor selector
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Cmd+K (Mac) ou Ctrl+K (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.openVendorSelectorModal();
    }
  }

  openVendorSelectorModal() {
    // Ne pas ouvrir la modal si un seul vendor
    if (this.vendors().length <= 1) {
      return;
    }

    const modalRef: ModalInstance = this.modalService.open(
      VendorSelectorModalComponent,
      {
        title: "Changer d'organisation",
        size: 'default',
        hideCancelAction: true,
        showActionButtons: false,
      },
    );

    // Passer les données au modal
    const modalComponent = modalRef.componentRef
      .instance as VendorSelectorModalComponent;
    modalComponent.setData({
      vendors: this.vendors(),
      currentVendorId: this.authUser()?.vendor?.id ?? null,
    });

    // Écouter la sélection
    modalComponent.vendorSelected$.subscribe((vendorId: number) => {
      this.changeVendor(vendorId);
      // Ne pas fermer la modal, le reload de la page le fera
    });
  }

  async changeVendor(vendorId: number) {
    const currentUser = this.authUser();

    if (currentUser?.vendor?.id === vendorId) return;

    try {
      this.store.dispatch(
        authActions.setVendor({
          vendor: this.authUser().vendors.find((el) => el.id === vendorId),
        }),
      );
      this.store.dispatch(bookingsActions.resetFilters());
      this.store.dispatch(authActions.findCurrentUser());
      const user = await firstValueFrom(
        this.store
          .select(selectAuthState)
          .pipe(skipWhile((state) => state.loading)),
      );
      if (user.user.role.name === UserRole.Member) {
        this.router.navigate(['/reservations']);
      } else if (this.router.url.match('\\/\\w*\\/\\w')) {
        this.router.navigate(['/', this.router.url.split('/')[1]]);
      }
      window.location.reload();
    } catch (exception) {
      console.error('Error changing vendor:', exception);
    }
  }
}
